import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { last, uniq } from 'lodash';

import { Icon } from '@/components/icon';

import styles from './tag-input.module.scss';

export interface TagInputProps {
  value: string[];
  placeholder?: string;
  highlighted?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  onChange?: (value: string[]) => void;
}

export const TagInput = (props: TagInputProps) => {
  const { value, placeholder = 'Value', autoFocus = false, required = false, highlighted } = props;

  const [inputValue, setInputValue] = useState('');
  const [forceCaretPosition, setForceCaretPosition] = useState<number | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (
      newValue.at(-1) === ',' &&
      inputValue.at(-1) !== ',' &&
      newValue.length > inputValue.length
    ) {
      props.onChange?.(uniq([...value, newValue.slice(0, -1).trim()]));
      setInputValue('');
    } else {
      setInputValue(newValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const content = e.clipboardData.getData('text');
    if (content.includes(',')) {
      e.preventDefault();
      const tags = content.split(',').map((tag) => tag.trim());
      props.onChange?.(uniq([...value, ...tags]));
    }
  };

  const handleInputBlur = () => {
    if (inputValue.length > 0) {
      props.onChange?.(uniq([...value, inputValue]));
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.length > 0) {
      e.preventDefault();
      props.onChange?.(uniq([...value, inputValue]));
      setInputValue('');
    } else if (
      ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z') && inputValue.length === 0) ||
      (e.key === 'Backspace' && ref.current?.selectionEnd === 0)
    ) {
      e.preventDefault();
      const lastTag = last(value);
      if (lastTag !== undefined) {
        props.onChange?.(value.slice(0, -1));
        setInputValue(`${lastTag},${inputValue}`);
        setForceCaretPosition(lastTag.length + 1);
      }
    }
  };

  useEffect(() => {
    if (forceCaretPosition !== null) {
      ref.current?.setSelectionRange(forceCaretPosition, forceCaretPosition);
      setForceCaretPosition(null);
    }
  }, [forceCaretPosition]);

  return (
    <ol
      aria-required={required}
      className={classNames(styles.tagInput, {
        [styles.highlighted]: highlighted,
      })}
      onClick={() => ref.current?.focus()}>
      {value.map((tag, index) => (
        <li key={index} className={styles.tag}>
          <span className={styles.label}>{tag}</span>
          <button
            type="button"
            title="Remove"
            className={styles.removeTag}
            onClick={() => {
              props.onChange?.(value.filter((_, i) => i !== index));
            }}>
            <Icon name="X" size={12} />
          </button>
        </li>
      ))}
      <li className={styles.newTag}>
        <input
          autoFocus={autoFocus}
          draggable={false}
          ref={ref}
          className={styles.input}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
      </li>
    </ol>
  );
};
